import { useContext, useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Dialog, Stack, TextField } from '@fluentui/react'
import { CopyRegular, NavigationRegular } from '@fluentui/react-icons'

import { CosmosDBStatus } from '../../api'
import { AppStateContext } from '../../state/AppProvider'
import { LeftNavBar } from '../../components/LeftNavBar/LeftNavBar'
import { buildMediaUrl } from '../../config/apiConfig'

import styles from './Layout.module.css'

const Layout = () => {
  const [isSharePanelOpen, setIsSharePanelOpen] = useState<boolean>(false)
  const [copyClicked, setCopyClicked] = useState<boolean>(false)
  const [copyText, setCopyText] = useState<string>('Copy URL')
  const [shareLabel, setShareLabel] = useState<string | undefined>('Share')
  const [hideHistoryLabel, setHideHistoryLabel] = useState<string>('Hide chat history')
  const [showHistoryLabel, setShowHistoryLabel] = useState<string>('Show chat history')
  const [logo, setLogo] = useState('')
  const [isMobileNavOpen, setIsMobileNavOpen] = useState<boolean>(false)
  const appStateContext = useContext(AppStateContext)
  const ui = appStateContext?.state.frontendSettings?.ui
  const location = useLocation();

  useEffect(() => {
    // Update logo and favicon whenever frontend settings are available/updated
    const fs = appStateContext?.state.frontendSettings?.ui
    if (fs) {
      setLogo(fs.logo || '')
      
      // Update page title
      if (fs.page_title) {
        document.title = fs.page_title
      }
      
      try {
        const favicon = (fs as any).favicon
        if (favicon) {
          // Build full URL for cross-origin favicon
          const faviconUrl = buildMediaUrl(favicon)
          const link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']")
          if (link) {
            link.href = faviconUrl
          } else {
            const newLink = document.createElement('link')
            newLink.rel = 'icon'
            newLink.href = faviconUrl
            document.getElementsByTagName('head')[0].appendChild(newLink)
          }
        }
      } catch (e) {
        // ignore
      }
    } else if (!appStateContext?.state.isLoading) {
      setLogo(ui?.logo || '')
    }
  }, [appStateContext?.state.frontendSettings, appStateContext?.state.isLoading])

  useEffect(() => {
    if (copyClicked) {
      setCopyText('Copied URL')
    }
  }, [copyClicked])

  useEffect(() => { }, [appStateContext?.state.isCosmosDBAvailable.status])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 480) {
        setShareLabel(undefined)
        setHideHistoryLabel('Hide history')
        setShowHistoryLabel('Show history')
      } else {
        setShareLabel('Share')
        setHideHistoryLabel('Hide chat history')
        setShowHistoryLabel('Show chat history')
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleShareClick = () => {
    setIsSharePanelOpen(true)
  }

  const handleSharePanelDismiss = () => {
    setIsSharePanelOpen(false)
    setCopyClicked(false)
    setCopyText('Copy URL')
  }

  const handleCopyClick = () => {
    navigator.clipboard.writeText(window.location.href)
    setCopyClicked(true)
  }

  const handleHistoryClick = () => {
    appStateContext?.dispatch({ type: 'TOGGLE_CHAT_HISTORY' })
  }

  // Determine selected nav key from route
  let selectedKey = 'chat';
  if (location.pathname.startsWith('/research')) selectedKey = 'research';
  else if (location.pathname.startsWith('/admin')) selectedKey = 'admin';
  else if (location.pathname.startsWith('/ui-admin')) selectedKey = 'ui-admin';
  else if (location.pathname.startsWith('/news-admin')) selectedKey = 'news-admin';
  else if (location.pathname.startsWith('/cost')) selectedKey = 'cost';
  else if (location.pathname.startsWith('/cloud')) selectedKey = 'cloud';
  else if (location.pathname.startsWith('/ai-connector-hub')) selectedKey = 'ai-connector-hub';
  else if (location.pathname.startsWith('/help')) selectedKey = 'help';
  
  // History panel is open on chat page - select history nav item
  if (location.pathname === '/' && appStateContext?.state.isChatHistoryOpen) {
    selectedKey = 'history';
  }

  // Check if navigation should be hidden
  const hideNavigation = ui?.hide_navigation || false;

  return (
    <div className={styles.layout}>
      {/* Mobile header with hamburger menu - only show if navigation is not hidden */}
      {!hideNavigation && (
        <div className={styles.mobileHeader}>
          <button
            onClick={() => setIsMobileNavOpen(true)}
            className={styles.mobileMenuButton}
            aria-label="Open navigation menu"
          >
            <NavigationRegular style={{ fontSize: 20 }} />
          </button>
          
          {logo && (
            <img src={buildMediaUrl(logo)} alt="Logo" className={styles.mobileHeaderLogo} />
          )}
          
          <div style={{ width: 40 }} /> {/* Spacer for balance */}
        </div>
      )}

      <div className={styles.mainContainer}>
        {/* Only render LeftNavBar if navigation is not hidden */}
        {!hideNavigation && (
          <LeftNavBar 
            selectedKey={selectedKey} 
            onSelect={() => {}} 
            isOpen={isMobileNavOpen}
            onClose={() => setIsMobileNavOpen(false)}
          />
        )}
        <div className={hideNavigation ? styles.contentFullWidth : styles.content}>
          <Outlet />
        </div>
      </div>
      <Dialog
        onDismiss={handleSharePanelDismiss}
        hidden={!isSharePanelOpen}
        styles={{
          main: [
            {
              selectors: {
                ['@media (min-width: 480px)']: {
                  maxWidth: '600px',
                  background: '#FFFFFF',
                  boxShadow: '0px 14px 28.8px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)',
                  borderRadius: '8px',
                  maxHeight: '200px',
                  minHeight: '100px'
                }
              }
            }
          ]
        }}
        dialogContentProps={{
          title: 'Share the web app',
          showCloseButton: true
        }}>
        <Stack horizontal verticalAlign="center" style={{ gap: '8px' }}>
          <TextField className={styles.urlTextBox} defaultValue={window.location.href} readOnly />
          <div
            className={styles.copyButtonContainer}
            role="button"
            tabIndex={0}
            aria-label="Copy"
            onClick={handleCopyClick}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? handleCopyClick() : null)}>
            <CopyRegular className={styles.copyButton} />
            <span className={styles.copyButtonText}>{copyText}</span>
          </div>
        </Stack>
      </Dialog>
    </div>
  )
}

export default Layout
